/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {CreativeApiService} from '../creative/creative-api.service';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {RouteTwoPanelLayoutComponent} from '../common/directives/route-two-panel-layout.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {LineItemAnalysisPageComponent} from './line-item-analysis-page.component';
import {LineItemContextActionsComponent} from './line-item-context-actions.component';
import {LineItemEditPageComponent} from './line-item-edit-page.component';
import {LineItemApiService, LineItemModel} from './line-item-api.service';
import {LineItemListContextActionsComponent} from './line-item-list-context-actions.component';
import {LineItemListPageComponent} from './line-item-list-page.component';

export function lineItemStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(LineItemApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey);
  const rh = adnRouterHelper.createRouterAndChildHelper(
    'line-items.line-item', LineItemApiService, CreativeApiService, 'app.adv', 'app'
  );

  states.add(rh.listPath(), rh.list({
    resolve: {
      sectionParam: function() {
        return 'lineItems';
      }
    },
    views: {
      'content@app': {component: LineItemListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {
        component: LineItemListContextActionsComponent,
        bindings: {
          readOnly: 'listReadOnly',
          sectionParam: 'sectionParam'
        }
      }
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      advertiserFromParam: function($stateParams: any) {
        if ($stateParams.advertiserId && $stateParams.advertiserName) {
          return {id: $stateParams.advertiserId, name: $stateParams.advertiserName};
        }
      },
      orderFromParam: function($stateParams: any) {
        if ($stateParams.orderId && $stateParams.orderName) {
          return {id: $stateParams.orderId, name: $stateParams.orderName};
        }
      },
      lineItemView: function($stateParams: any) {
        if (!$stateParams.isNew) {
          const lineItemId = $stateParams.id || 'undefined-id';
          return firstValueFrom(
            adnRouterHelper.services.dataViewApi().getView('LineItemView', {id: lineItemId}, false)
          ).catch(() => null);
        }
      },
      full: function($stateParams: any) {
        return !!$stateParams.full;
      },
      viewType: function($stateParams: any) {
        return $stateParams.viewType === 2 ? 2 : 1;
      },
      lineItemPage: function($stateParams: any, lineItemView: any, advertiserFromParam: any, orderFromParam: any) {
        const createState = (model: LineItemModel, isNew: boolean) => {
          let page: SignalAdminResourcePageState<LineItemModel>;
          const crumbParams = [
            {
              type: 'Advertiser',
              resolve: (data: LineItemModel) => page.relatedData().lineItemBreadcrumbs?.advertiser
                || data.advertiser || data.order?.advertiser
            },
            {
              type: 'Order',
              resolve: (data: LineItemModel) => page.relatedData().lineItemBreadcrumbs?.order || data.order
            }
          ];
          page = new SignalAdminResourcePageState(
            model, isNew, readOnly(), api, 'Line Item', 'name', undefined, undefined,
            {objectTypeKey: 'LineItem', crumbParams},
            {warningMessage: 'The {{action}} of the Line Item will also stop it from serving ads.', object: 'Line Item'}
          );
          page.updateRelatedData('lineItemBreadcrumbs', {
            advertiser: lineItemView?.advertiser || model.advertiser || model.order?.advertiser || advertiserFromParam,
            order: lineItemView?.order || model.order || orderFromParam
          });
          page.setPanelFull(!!$stateParams.full);
          return page;
        };
        if ($stateParams.isNew) {
          return createState({
            id: $stateParams.id,
            userState: 'RESERVED',
            resourceName: 'lineItem',
            objectState: 'ACTIVE',
            validationWarnings: []
          }, true);
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => createState({...model, resourceName: 'lineItem'}, false));
      },
      model: (lineItemPage: SignalAdminResourcePageState<LineItemModel>) => lineItemPage.model()
    },
    views: {
      'content@app': {
        component: RouteTwoPanelLayoutComponent,
        bindings: {full: 'full', state: 'lineItemPage'}
      },
      'rightPanel@app.adv.line-items.line-item': {
        component: LineItemAnalysisPageComponent,
        bindings: {state: 'lineItemPage', lineItemView: 'lineItemView'}
      },
      'leftPanel@app.adv.line-items.line-item': {
        component: LineItemEditPageComponent,
        bindings: {
          state: 'lineItemPage', full: 'full', viewType: 'viewType',
          orderFromParam: 'orderFromParam', advertiserFromParam: 'advertiserFromParam', lineItemView: 'lineItemView'
        }
      },
      'leftPanelPartA@app.adv.line-items.line-item': {
        component: LineItemEditPageComponent,
        bindings: {
          state: 'lineItemPage', full: 'full', viewType: 'viewType',
          orderFromParam: 'orderFromParam', advertiserFromParam: 'advertiserFromParam', lineItemView: 'lineItemView'
        }
      },
      'context@app': {
        component: LineItemContextActionsComponent,
        bindings: {state: 'lineItemPage', viewType: 'viewType'}
      },
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'lineItemPage'}}
    }
  }, 'LineItem'));
  return states.build();
}
